/**
 * AgentFlow — canvas-authored flow definition (stage-based).
 *
 * This is the single contract between the visual canvas (which writes
 * a `FlowDefinition`) and the flow-to-agent compiler (which reads one).
 * The web and api packages both depend on this module so a drift here
 * shows up as a typecheck error, not a runtime surprise.
 *
 * The schema is versioned. The compiler accepts only the current
 * `schemaVersion`. Breaking changes bump the version and require a
 * migration script.
 *
 * Determinism rule: the compiler is pure (no IO, no Date, no Math.random).
 * That means everything that influences compiled output must live in this
 * type — if the canvas can't express it, the compiler doesn't see it.
 *
 * Model: a flow is an explicit stage machine. Stages are nodes (each a
 * micro-agent gated to its own tools), transitions are deterministic edges,
 * `slots` are the persisted facts a stage collects, and a terminal stage is
 * the conversation's goal. See docs/adr/0026.
 */
import { z } from "zod";
// ---------------------------------------------------------------------------
// Conversation channels.
//
// MUST stay in lockstep with `AGENT_CONVERSATION_CHANNELS` in
// `packages/db/src/models/AgentConversation.ts`. `shared` and `db` are
// independent packages (neither depends on the other), so this list cannot
// import that enum directly — a test in apps/api, which sees both, asserts
// they match.
//
// This list previously read ["sms", "rcs", "sandbox"] and silently fell three
// channels behind the database (whatsapp, voice, webhook). It was copied into
// the conversations-list query validator and the UI's filter dropdown, so
// WhatsApp conversations could not be filtered for at any layer and the API
// rejected `?channel=whatsapp` with a 400. Consume this constant — never
// re-type the list.
// ---------------------------------------------------------------------------
export const CONVERSATION_CHANNELS = [
    "sms",
    "rcs",
    "whatsapp",
    "voice",
    "webhook",
    "sandbox",
];
/** Human labels for channel pickers. Keyed so a new channel can't be forgotten. */
export const CONVERSATION_CHANNEL_LABELS = {
    sms: "SMS",
    rcs: "RCS",
    whatsapp: "WhatsApp",
    voice: "Voice",
    webhook: "Webhook",
    sandbox: "Sandbox",
};
// ---------------------------------------------------------------------------
// Tool ids — kept narrow on purpose. Adding one here without registering it
// in the api's tool registry will make the compiler throw.
// ---------------------------------------------------------------------------
export const TOOL_IDS = [
    "book_appointment",
    "qualify_lead",
    "faq_lookup",
    "escalate_to_human",
];
// ---------------------------------------------------------------------------
// Guardrails
// ---------------------------------------------------------------------------
export const GUARDRAIL_KINDS = ["never_say", "must_confirm_before", "stay_on_topic"];
export const NeverSayGuardrailSchema = z
    .object({
    id: z.string().min(1),
    kind: z.literal("never_say"),
    phrases: z.array(z.string().min(1)).min(1),
})
    .strict();
export const MustConfirmBeforeGuardrailSchema = z
    .object({
    id: z.string().min(1),
    kind: z.literal("must_confirm_before"),
    /** Action ids that require a confirmation step before execution. */
    actionIds: z.array(z.string().min(1)).min(1),
})
    .strict();
export const StayOnTopicGuardrailSchema = z
    .object({
    id: z.string().min(1),
    kind: z.literal("stay_on_topic"),
    topics: z.array(z.string().min(1)).min(1),
})
    .strict();
export const GuardrailSchema = z.discriminatedUnion("kind", [
    NeverSayGuardrailSchema,
    MustConfirmBeforeGuardrailSchema,
    StayOnTopicGuardrailSchema,
]);
// ---------------------------------------------------------------------------
// Action
// ---------------------------------------------------------------------------
export const ActionSchema = z
    .object({
    id: z.string().min(1),
    toolId: z.enum(TOOL_IDS),
    /**
     * Tool-specific config. Validated by the api's tool registry against
     * the tool's own Zod schema at compile time — the canvas doesn't need
     * to enforce shape, only that it's an object.
     *
     * Defaults to `{}` when omitted: some tools (e.g. `faq_lookup`,
     * `escalate_to_human` with no message) take no required config, and the
     * builder legitimately emits actions without a `config` key. The api
     * still re-validates the resolved object against each tool's own schema.
     */
    config: z.record(z.unknown()).default({}),
    requiresConfirmation: z.boolean(),
    /**
     * Per-action opt-in for the action-event webhook. When true (and the
     * tenant has the webhook enabled + configured), the runtime POSTs a
     * signed `agentflow.action` event after this action runs. Optional for
     * backward compatibility with flows authored before this field —
     * absent is treated as `false`.
     */
    emitEvent: z.boolean().optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Business hours
// ---------------------------------------------------------------------------
const HHMM = z.string().regex(/^\d{2}:\d{2}$/, "Must be HH:MM (24-hour)");
export const BusinessHoursDayRowSchema = z
    .object({
    /** 0=Sunday … 6=Saturday, matching JS getDay(). */
    day: z.number().int().min(0).max(6),
    openHHMM: HHMM,
    closeHHMM: HHMM,
})
    .strict();
export const BusinessHoursSchema = z
    .object({
    /** IANA tz, e.g. "America/Chicago". The compiler does not parse it; the
     *  api applies it at request time to evaluate `escalation.afterHours`. */
    timezone: z.string().min(1),
    weekly: z.array(BusinessHoursDayRowSchema),
})
    .strict();
// ---------------------------------------------------------------------------
// Escalation
// ---------------------------------------------------------------------------
export const EscalationSchema = z
    .object({
    /** Action id to run when an inbound message arrives outside business hours. */
    afterHours: z.string().min(1).optional(),
    /** Action id to run when the agent can't make progress (tool error, hop cap, etc.). */
    onFailure: z.string().min(1).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Slot — a single fact the flow collects and persists across turns. Referenced
// by stages via `requiredSlotIds`; the runtime fills these and never re-asks a
// filled one (the fix for the "re-asking / looping" failure mode).
// ---------------------------------------------------------------------------
/**
 * Where a slot's value comes from:
 *  - "ask"           — the agent collects it in conversation (default).
 *  - "channel_phone" — auto-filled from the customer's phone number, which is
 *                      always available inbound on SMS/WhatsApp. The agent
 *                      confirms it instead of asking.
 *  - "channel_name"  — auto-filled from the WhatsApp profile name when present.
 *                      SMS carries no name, so the agent falls back to `prompt`.
 */
export const SLOT_SOURCES = ["ask", "channel_phone", "channel_name"];
/**
 * Slot value type (ADR 0027):
 *  - "text"   — free text (default).
 *  - "choice" — one of a fixed `options` enum (reliable for eq/in branching).
 *  - "number" — a number, optionally bounded by `min`/`max`. Enables numeric
 *               branching (gt/gte/lt/lte). Blank max = no upper bound
 *               ("any positive" = min 0/1, no max).
 */
export const SLOT_TYPES = ["text", "choice", "number"];
/**
 * Optional format hint for `type: "text"` slots. Enables deterministic,
 * LLM-free extraction + canonicalization in the runtime:
 *  - "phone" — normalized to E.164 (a US 10-digit number → +1…).
 *  - "email" — validated and lowercased.
 * Omitted = plain free text. Only valid on text slots.
 */
export const SLOT_FORMATS = ["phone", "email"];
/**
 * A generated NEGATIVE (counter) few-shot: a tempting but WRONG agent reply to
 * the same customer message as its parent example, plus a short reason naming
 * the mistake. Generated once at publish (fill-missing) and cached on the
 * example. `sourceHash` fingerprints the parent's `agent` line so it's
 * regenerated only when the author materially edits that line — never on
 * unrelated saves/republishes.
 */
export const ExampleCounterSchema = z
    .object({
    agent: z.string().min(1).max(500),
    reason: z.string().max(300).optional(),
    sourceHash: z.string().min(1).max(64),
})
    .strict();
/**
 * One few-shot turn (ADR 0029). The author writes only the `agent` line; the
 * platform generates a matching `customer` message and, at publish, a
 * `counterExample`. Shared by stage `examples` and slot `askExamples`.
 */
export const ExampleTurnSchema = z
    .object({
    customer: z.string().max(500).optional(),
    agent: z.string().min(1).max(500),
    counterExample: ExampleCounterSchema.optional(),
})
    .strict();
export const SlotSchema = z
    .object({
    id: z.string().min(1),
    /** How the agent should ask for this fact when it's missing. */
    prompt: z.string().min(1).max(500),
    /** Whether the slot must be filled before a stage requiring it can advance. */
    required: z.boolean().default(true),
    /**
     * Origin of the value. Channel-sourced slots are seeded at conversation
     * start from inbound metadata, so the agent confirms rather than re-asks.
     * Defaults to "ask" — existing flows are unaffected.
     */
    source: z.enum(SLOT_SOURCES).default("ask"),
    /** Value type. Defaults to "text" — existing flows are unaffected. */
    type: z.enum(SLOT_TYPES).default("text"),
    /**
     * Optional format for `type: "text"` slots (phone/email). Drives
     * deterministic extraction + canonicalization. Ignored for other types.
     */
    format: z.enum(SLOT_FORMATS).optional(),
    /**
     * Allowed-value set for `type: "choice"`. Extraction is constrained to one
     * of these canonical values, making value-based branching reliable.
     */
    options: z.array(z.string().min(1)).max(50).optional(),
    /** Inclusive bounds for `type: "number"`. Either may be omitted (open-ended). */
    min: z.number().optional(),
    max: z.number().optional(),
    /**
     * Few-shot phrasings for HOW to ask for this slot (ADR 0029). The author
     * writes the agent line; the platform generates a matching customer answer.
     * Because slots are referenced by many stages, this "how to collect this
     * value" guidance travels with the slot — the runtime injects it as examples
     * in every stage that collects the slot, instead of per-stage authoring.
     */
    askExamples: z.array(ExampleTurnSchema).max(3).optional(),
})
    .strict()
    .superRefine((s, ctx) => {
    if (s.type === "number" && s.min !== undefined && s.max !== undefined && s.min > s.max) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["min"],
            message: `Slot "${s.id}": min (${s.min}) cannot be greater than max (${s.max}).`,
        });
    }
    if (s.format !== undefined && s.type !== "text") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["format"],
            message: `Slot "${s.id}": format is only valid for type "text".`,
        });
    }
});
/**
 * A reference to an account-level catalog slot by key (ADR 0030). The compiler
 * resolves it to a concrete `Slot` from the tenant's slot catalog at runtime;
 * `ref` doubles as the slot id in `conversation.slots` and in `requiredSlotIds`.
 */
export const SlotRefSchema = z.object({ ref: z.string().min(1) }).strict();
/**
 * A slot entry in a flow: either a catalog reference (`{ ref }`) or a flow-local
 * inline `Slot`. Disjoint — a `{ ref }` object fails the strict `SlotSchema`
 * (no id/prompt) and vice-versa.
 */
export const SlotEntrySchema = z.union([SlotRefSchema, SlotSchema]);
/** True when a slot entry is a catalog reference rather than an inline slot. */
export function isSlotRef(entry) {
    return "ref" in entry;
}
/** The effective slot id/key of an entry (the `ref` key or the inline `id`). */
export function slotEntryKey(entry) {
    return isSlotRef(entry) ? entry.ref : entry.id;
}
// ---------------------------------------------------------------------------
// Stage transition — one outgoing edge. Deterministic: evaluated by the
// runtime, not the model. `on` is the trigger; `toStage` is the next stage id.
// ---------------------------------------------------------------------------
export const STAGE_TRANSITION_TRIGGERS = [
    "action_success", // a stage action's tool ran successfully
    "slots_complete", // all of the stage's requiredSlotIds are filled
    "no_progress", // the stage's turn budget was exhausted (escape hatch)
    "slot_condition", // a slot's value matches a condition (ADR 0027)
];
// Value-based branching operators (ADR 0027).
//  - `eq`/`in`: compare the extracted value (case-insensitive, trimmed).
//  - `exists`: matches any non-empty value.
//  - `gt`/`gte`/`lt`/`lte`: numeric comparison (for `type: "number"` slots).
export const SLOT_CONDITION_OPS = ["eq", "in", "exists", "gt", "gte", "lt", "lte"];
/** Numeric comparison operators — `value` must parse to a number. */
export const NUMERIC_SLOT_CONDITION_OPS = ["gt", "gte", "lt", "lte"];
export const SlotConditionSchema = z
    .object({
    /** Slot whose value is tested. Must reference a declared slot. */
    slotId: z.string().min(1),
    op: z.enum(SLOT_CONDITION_OPS),
    /**
     * `eq` / gt/gte/lt/lte: a single value (numeric ops parse it as a number).
     * `in`: a non-empty list. `exists`: omitted.
     */
    value: z.union([z.string().min(1), z.array(z.string().min(1)).min(1)]).optional(),
})
    .strict();
export const StageTransitionSchema = z
    .object({
    on: z.enum(STAGE_TRANSITION_TRIGGERS),
    /** Required when `on === "action_success"`: which action must have fired. */
    actionId: z.string().min(1).optional(),
    /** Required when `on === "slot_condition"`: the value test (ADR 0027). */
    condition: SlotConditionSchema.optional(),
    /** Destination stage id. Must reference a defined stage. */
    toStage: z.string().min(1),
})
    .strict()
    .superRefine((t, ctx) => {
    if (t.on !== "slot_condition")
        return;
    if (!t.condition) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["condition"],
            message: "A slot_condition transition requires a `condition`.",
        });
        return;
    }
    const { op, value } = t.condition;
    if (op === "eq" && typeof value !== "string") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["condition", "value"],
            message: "`eq` requires a single string value.",
        });
    }
    if (op === "in" && !Array.isArray(value)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["condition", "value"],
            message: "`in` requires a non-empty list of values.",
        });
    }
    if (NUMERIC_SLOT_CONDITION_OPS.includes(op)) {
        if (typeof value !== "string" || Number.isNaN(Number(value))) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["condition", "value"],
                message: `\`${op}\` requires a single numeric value.`,
            });
        }
    }
});
// ---------------------------------------------------------------------------
// Stage — a node in the graph. `kind: "terminal"` marks a goal/exit (END);
// terminal stages run no tools and have no outgoing transitions.
// ---------------------------------------------------------------------------
export const STAGE_KINDS = ["agent", "terminal", "subflow"];
/**
 * Stage archetype (ADR 0029). Drives how the stage's `<current_task>` prompt is
 * compiled — each type has a tailored, generated objective instead of free
 * prose. `custom` keeps the free-text `objective` (back-compat + escape hatch);
 * existing flows have no `type` and default to it, so they compile unchanged.
 */
export const STAGE_TYPES = [
    "identify_intent",
    "collect_info",
    "pitch",
    "action",
    "wrap_up",
    "custom",
];
/**
 * Platform goal types for `action`/terminal stages (ADR 0029). `handoff` maps to
 * the escalation machinery (ADR 0015), not a bespoke stage.
 */
export const GOAL_TYPES = ["book", "quote", "qualify", "handoff", "custom"];
/**
 * Platform default few-shot example turns per typed stage (ADR 0029). Seeded
 * into a stage when the author picks a type, and used as a runtime fallback when
 * a typed stage carries none — so a new flow behaves well out of the box. Style
 * only; authors edit freely. `custom` gets none (it uses the free-text objective).
 */
export const STAGE_TYPE_EXAMPLE_DEFAULTS = {
    identify_intent: [
        {
            customer: "Hi, I need pest control",
            agent: "Happy to help with that! Is this for your home or a business?",
        },
    ],
    collect_info: [
        {
            customer: "it's a 3 bedroom house",
            agent: "Got it, thanks. And what's the best phone number to reach you?",
        },
    ],
    pitch: [
        {
            customer: "why should I go with you?",
            agent: "Great question! We're licensed and insured, our crews are background-checked, and you get a clear upfront estimate — no surprises. Want me to walk you through the next steps?",
        },
    ],
    action: [{ customer: "yes that works", agent: "Perfect — I'm getting that set up for you now." }],
    wrap_up: [
        {
            customer: "what happens next?",
            agent: "Our team will reach out shortly to confirm the details. Is there anything else I can help you with?",
        },
    ],
    custom: [],
};
export const StageSchema = z
    .object({
    id: z.string().min(1),
    kind: z.enum(STAGE_KINDS).default("agent"),
    /**
     * Stage archetype (ADR 0029). Omitted = treated as `custom`, so existing
     * flows (which have no `type`) compile from the free-text `objective`
     * exactly as before. Typed stages generate their objective from config.
     */
    type: z.enum(STAGE_TYPES).optional(),
    /** For `action`/terminal stages: which platform goal this performs. */
    goalType: z.enum(GOAL_TYPES).optional(),
    /**
     * Stage-scoped objective. Authoritative for `type: "custom"`; for typed
     * stages the objective is generated from config and this is ignored (kept so
     * switching a stage back to `custom` doesn't lose the text).
     */
    objective: z.string().min(1).max(2000),
    /**
     * Goal this terminal stage represents. Reaching a terminal stage records a
     * "goal reached" outcome keyed by `goal.id`. A flow can have several
     * terminal stages, each a distinct goal (lead captured, booked, answered,
     * handed off). `capture` optionally also writes a typed record. Ignored on
     * non-terminal stages; if omitted on a terminal stage, the goal id defaults
     * to the stage id at runtime.
     */
    goal: z
        .object({
        id: z.string().min(1).max(80),
        label: z.string().max(160).optional(),
        capture: z.enum(["none", "lead"]).optional(),
        /** Post an `agentflow.goal_reached` event to the account webhook on reach. */
        emitEvent: z.boolean().optional(),
    })
        .strict()
        .optional(),
    /**
     * Optional author override: hard "do NOT" rules scoped to this stage,
     * injected into the stage prompt with precedence over the persona. The
     * engine already auto-scopes from requiredSlotIds; this is the escape hatch
     * for boundaries that can't be derived from slots (e.g. "don't discuss
     * pricing here", "don't promise timelines").
     */
    boundaries: z.string().max(2000).optional(),
    /** Tools legal IN this stage. Empty = conversational only (no tools). */
    allowedActionIds: z.array(z.string().min(1)).max(20).default([]),
    /**
     * Actions the agent MUST actually invoke before this stage can complete via
     * slot completion. Use for goals that require a real side effect — e.g.
     * `book_appointment` — so the agent cannot self-report success ("you're all
     * booked!") without genuinely calling the tool. The runtime (a) injects a
     * forceful "call the tool, never fake it" mandate into the stage prompt, and
     * (b) suppresses any `slots_complete` transition until every required action
     * has fired. Must be a subset of `allowedActionIds`. Ignored on terminal
     * stages (which bind no tools). Empty = no mandatory action.
     */
    requiredActionIds: z.array(z.string().min(1)).max(20).default([]),
    /** Slots that must be filled before a `slots_complete` transition fires. */
    requiredSlotIds: z.array(z.string().min(1)).max(50).default([]),
    /**
     * Sub-flow connection (ADR 0030) — only on `kind: "subflow"`. The compiler
     * inlines the referenced flow's latest published definition at this node.
     */
    flowRef: z.string().min(1).optional(),
    /**
     * Sub-flow exit map — only on `kind: "subflow"`. Maps a sub-flow terminal
     * stage id → this (parent) flow's continuation stage id. In the combined
     * graph the sub-flow terminal becomes a pass-through to that parent stage;
     * unmapped sub-flow terminals stay terminal (end the whole conversation).
     */
    exits: z.record(z.string(), z.string()).optional(),
    /**
     * Few-shot example turns for this stage (ADR 0029) — compiled into an
     * `<example>` block so the model learns HOW to ask in this stage's voice.
     * Applies to every type. Platform ships defaults per type; author edits.
     */
    // Author writes only the agent's line; the platform generates a matching
    // customer message (ADR 0029) and, at publish, a `counterExample` (do/don't).
    // Optional so an example can be saved agent-only before/without generation.
    examples: z.array(ExampleTurnSchema).max(10).optional(),
    /** Stage-scoped phrases the agent must never say — merged with the flow-global `never_say`. */
    neverSay: z.array(z.string().min(1).max(200)).max(50).optional(),
    /**
     * Whether `faq_lookup` is available in this stage. Undefined = default on for
     * typed stages, off for `custom` (so existing flows' tool binding is
     * unchanged). Only takes effect when the flow declares a `faq_lookup` action.
     */
    faqEnabled: z.boolean().optional(),
    transitions: z.array(StageTransitionSchema).max(20).default([]),
    /**
     * No-progress budget: max turns the conversation may spend in this stage
     * before a `no_progress` transition is forced (→ typically escalation).
     * Omitted = inherit the flow-level default at compile time.
     */
    maxTurns: z.number().int().positive().max(50).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Top-level FlowDefinition
// ---------------------------------------------------------------------------
export const FLOW_DEFINITION_SCHEMA_VERSION = 2;
export const FlowDefinitionSchema = z
    .object({
    schemaVersion: z.literal(FLOW_DEFINITION_SCHEMA_VERSION),
    /** Tone + identity. `{businessName}` is the only allowed interpolation. */
    persona: z.string().min(1).max(2000),
    /** First outbound message when an agent initiates the conversation. */
    greeting: z.string().min(1).max(1000),
    /**
     * The facts this flow collects, referenced by stages. Each entry is either a
     * catalog reference (`{ ref: key }`, ADR 0030) or an inline flow-local slot.
     * References resolve to concrete slots at compile time.
     */
    slots: z.array(SlotEntrySchema).max(50).default([]),
    actions: z.array(ActionSchema).max(50),
    guardrails: z.array(GuardrailSchema).max(20).default([]),
    stages: z.array(StageSchema).min(1).max(50),
    /** The stage a new conversation starts in. Must reference a defined stage. */
    entryStageId: z.string().min(1),
    /** Flow-level no-progress default for stages that omit `maxTurns`. */
    defaultStageMaxTurns: z.number().int().positive().max(50).default(8),
    /**
     * Canvas node positions, keyed by stage id. Authoring-only — the runtime
     * ignores it. Persisted so a stage's placement survives reloads. Stages
     * without an entry fall back to the deterministic auto-layout.
     */
    layout: z.record(z.object({ x: z.number(), y: z.number() }).strict()).default({}),
    businessHours: BusinessHoursSchema.optional(),
    escalation: EscalationSchema.optional(),
})
    .strict()
    .superRefine((def, ctx) => {
    const stageIds = new Set(def.stages.map((s) => s.id));
    const actionIds = new Set(def.actions.map((a) => a.id));
    // A slot entry declares an id via its inline `id` or its catalog `ref` key.
    const slotIds = new Set(def.slots.map(slotEntryKey));
    assertUniqueIds(def.stages.map((s) => s.id), "stages", ctx);
    assertUniqueIds(def.slots.map(slotEntryKey), "slots", ctx);
    assertUniqueIds(def.actions.map((a) => a.id), "actions", ctx);
    // entryStageId must resolve.
    if (!stageIds.has(def.entryStageId)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["entryStageId"],
            message: `entryStageId "${def.entryStageId}" does not match any stage.`,
        });
    }
    // The flow needs a goal: at least one terminal stage.
    if (!def.stages.some((s) => s.kind === "terminal")) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["stages"],
            message: "A flow needs at least one terminal stage (the goal/exit).",
        });
    }
    // must_confirm_before.actionIds → must all reference defined actions
    for (const g of def.guardrails) {
        if (g.kind === "must_confirm_before") {
            for (const aid of g.actionIds) {
                if (!actionIds.has(aid)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ["guardrails", g.id, "actionIds"],
                        message: `Guardrail references action id "${aid}" which does not exist.`,
                    });
                }
            }
        }
    }
    // escalation.afterHours / onFailure → must reference defined actions
    if (def.escalation?.afterHours && !actionIds.has(def.escalation.afterHours)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["escalation", "afterHours"],
            message: `Escalation afterHours references action id "${def.escalation.afterHours}" which does not exist.`,
        });
    }
    if (def.escalation?.onFailure && !actionIds.has(def.escalation.onFailure)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["escalation", "onFailure"],
            message: `Escalation onFailure references action id "${def.escalation.onFailure}" which does not exist.`,
        });
    }
    for (const stage of def.stages) {
        const at = (k) => ["stages", stage.id, k];
        // Sub-flow connection nodes (ADR 0030): route via `exits`, not their own
        // transitions/tools. `flowRef` is validated at compile (exists/published/
        // no-cycle); here we check the shape + that exit targets resolve.
        if (stage.kind === "subflow") {
            if (!stage.flowRef) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: at("flowRef"),
                    message: `Sub-flow stage "${stage.id}" must set flowRef.`,
                });
            }
            if (stage.transitions.length > 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: at("transitions"),
                    message: `Sub-flow stage "${stage.id}" routes via exits, not transitions.`,
                });
            }
            if (stage.allowedActionIds.length > 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: at("allowedActionIds"),
                    message: `Sub-flow stage "${stage.id}" must not bind tools.`,
                });
            }
            for (const target of Object.values(stage.exits ?? {})) {
                if (!stageIds.has(target)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: at("exits"),
                        message: `Sub-flow stage "${stage.id}" exit points to "${target}" which is not a stage.`,
                    });
                }
            }
        }
        // Terminal stages are dead-ends by definition.
        if (stage.kind === "terminal") {
            if (stage.transitions.length > 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: at("transitions"),
                    message: `Terminal stage "${stage.id}" must have no transitions.`,
                });
            }
            if (stage.allowedActionIds.length > 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: at("allowedActionIds"),
                    message: `Terminal stage "${stage.id}" must not bind tools.`,
                });
            }
            if (stage.requiredActionIds.length > 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: at("requiredActionIds"),
                    message: `Terminal stage "${stage.id}" must not require actions — it binds no tools.`,
                });
            }
        }
        for (const aid of stage.allowedActionIds) {
            if (!actionIds.has(aid)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: at("allowedActionIds"),
                    message: `Stage "${stage.id}" allows action "${aid}" which does not exist.`,
                });
            }
        }
        // A required action must be one this stage is actually allowed to use —
        // otherwise the agent is told to call a tool it cannot see, and the
        // slots_complete gate could never open.
        for (const aid of stage.requiredActionIds) {
            if (!stage.allowedActionIds.includes(aid)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: at("requiredActionIds"),
                    message: `Stage "${stage.id}" requires action "${aid}" that it does not allow (add it to allowedActionIds).`,
                });
            }
        }
        for (const sid of stage.requiredSlotIds) {
            if (!slotIds.has(sid)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: at("requiredSlotIds"),
                    message: `Stage "${stage.id}" requires slot "${sid}" which does not exist.`,
                });
            }
        }
        for (const t of stage.transitions) {
            if (!stageIds.has(t.toStage)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: at("transitions"),
                    message: `Stage "${stage.id}" transitions to "${t.toStage}" which does not exist.`,
                });
            }
            if (t.on === "action_success") {
                if (!t.actionId) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: at("transitions"),
                        message: `Stage "${stage.id}" has an action_success transition with no actionId.`,
                    });
                }
                else if (!stage.allowedActionIds.includes(t.actionId)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: at("transitions"),
                        message: `Stage "${stage.id}" transitions on action "${t.actionId}" that it doesn't allow.`,
                    });
                }
            }
            if (t.on === "slot_condition" && t.condition && !slotIds.has(t.condition.slotId)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: at("transitions"),
                    message: `Stage "${stage.id}" branches on slot "${t.condition.slotId}" which does not exist.`,
                });
            }
        }
    }
});
/**
 * Publish-time reachability check. Returns the ids of `agent` stages that
 * cannot reach a terminal stage by following transitions. A conversation that
 * parks in such a stage never completes, so no goal / outcome / lead / webhook
 * is ever recorded (all of which gate on reaching a terminal goal stage).
 *
 * Deliberately NOT part of `FlowDefinitionSchema.superRefine`: that schema
 * validates every SAVE, and an in-progress draft may legitimately have a
 * not-yet-wired stage. This is a publish gate (`publishFlow`) instead.
 *
 * Sub-flow stages can always end the conversation via their child terminals,
 * so a sub-flow (and any path into one) counts as "can terminate".
 */
export function findUnreachableTerminalStages(def) {
    const stageIds = new Set(def.stages.map((s) => s.id));
    // Sinks that end (or can end) a conversation.
    const canEnd = new Set(def.stages.filter((s) => s.kind === "terminal" || s.kind === "subflow").map((s) => s.id));
    const outEdges = new Map();
    for (const s of def.stages) {
        const targets = s.kind === "subflow" ? Object.values(s.exits ?? {}) : s.transitions.map((t) => t.toStage);
        outEdges.set(s.id, targets.filter((id) => stageIds.has(id)));
    }
    // Backward fixpoint: a stage can end if any of its out-edges can end.
    let changed = true;
    while (changed) {
        changed = false;
        for (const s of def.stages) {
            if (canEnd.has(s.id))
                continue;
            if ((outEdges.get(s.id) ?? []).some((t) => canEnd.has(t))) {
                canEnd.add(s.id);
                changed = true;
            }
        }
    }
    return def.stages.filter((s) => s.kind === "agent" && !canEnd.has(s.id)).map((s) => s.id);
}
function assertUniqueIds(ids, path, ctx) {
    const seen = new Set();
    for (const id of ids) {
        if (seen.has(id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [path],
                message: `Duplicate id "${id}".`,
            });
        }
        seen.add(id);
    }
}
// ---------------------------------------------------------------------------
// Convenience — a fresh empty draft. Used by "Activate" + by tests.
// A minimal valid flow: one agent entry stage that falls through to a terminal
// on the no-progress budget so a fresh draft always terminates.
// ---------------------------------------------------------------------------
export function emptyFlowDefinition() {
    return {
        schemaVersion: FLOW_DEFINITION_SCHEMA_VERSION,
        persona: "You are a helpful assistant.",
        greeting: "Hi — how can I help?",
        slots: [],
        actions: [],
        guardrails: [],
        stages: [
            {
                id: "main",
                kind: "agent",
                objective: "Understand the customer's need and help them.",
                allowedActionIds: [],
                requiredActionIds: [],
                requiredSlotIds: [],
                transitions: [{ on: "no_progress", toStage: "done" }],
                maxTurns: 8,
            },
            {
                id: "done",
                kind: "terminal",
                objective: "Confirm what happens next and close warmly.",
                allowedActionIds: [],
                requiredActionIds: [],
                requiredSlotIds: [],
                transitions: [],
            },
        ],
        entryStageId: "main",
        defaultStageMaxTurns: 8,
        layout: {},
    };
}
//# sourceMappingURL=flow.js.map